import { createSelector } from 'reselect'

import { VALID_ACTIVITIES, VALID_USER_ACTIVITIES, VALID_ACTIVITIES_FOR_PANEL, VALID_ACTIVITIES_FOR_INBOX, NO_FLAG_ACTIVITIES, filterNotifications, getUnreadActivities, getNotificationsForProjects, getLastUserActivity, notificationIsUnread, getTaskNotifications } from 'utils/notification';

import { Notification, NotificationType, Project } from 'types';

const getNonMessageNotificationsByAssociationId = (notifications: Notification[], associationId: string) => filterNotifications(notifications, { associationId }).filter(notification => !notification?.association?.message_id);
export const makeGetNonMessageNotificationsByAssociationId = () => createSelector(getNonMessageNotificationsByAssociationId, notifications => notifications);

const getAllProjectNotifications = (notifications: Notification[]) => getNotificationsForProjects(notifications);
export const makeGetAllProjectNotifications = () => createSelector(getAllProjectNotifications, notifications => notifications);

const getProjectNotificationsByType = (notifications: Notification[], projectId: string, type: NotificationType) => notifications
    .filter(notification =>
        notificationIsUnread(notification) &&
        !NO_FLAG_ACTIVITIES.includes(`${notification.type}.${notification.action}`) &&
        notification.project &&
        notification.project.id === projectId &&
        notification.type === type
    );
export const makeGetProjectNotificationsByType = () => createSelector(getProjectNotificationsByType, notifications => notifications);

export const getNotificationsByAssociationId = (notifications: Notification[], associationId: string) => notifications
    .filter(notification =>
        notificationIsUnread(notification) &&
        notification.association &&
        (
            notification.association.id === associationId ||
            notification.association.chat_id === associationId
        )
    );
export const makeGetNotificationsByAssociationId = () => createSelector(getNotificationsByAssociationId, notifications => notifications);

const getMessageNotificationsByAssociationId = (notifications: Notification[], associationId: string) => notifications
    .filter(notification =>
        notificationIsUnread(notification) &&
        notification?.association?.message_id &&
        (
            notification.association.id === associationId ||
            notification.association.chat_id === associationId
        )
    );
export const makeGetMessageNotificationsByAssociationId = () => createSelector(getMessageNotificationsByAssociationId, notifications => notifications);

const getTeamMessageNotifications = (notifications: Notification[]) => notifications
    .filter(notification =>
        notificationIsUnread(notification) &&
        notification?.association?.message_id &&
        (
            notification?.association?.type === 'Chat' ||
            notification?.association?.type === 'Message'
        ) &&
        !notification.project
    );
export const makeGetTeamMessageNotifications = () => createSelector(getTeamMessageNotifications, notifications => notifications);

export const makeGetTaskNotifications = () => createSelector(getTaskNotifications, notifications => notifications);

const getNotificationsByType = (notifications: Notification[], type: NotificationType) => filterNotifications(notifications, { type });
export const makeGetNotificationsByType = () => createSelector(getNotificationsByType, notifications => notifications);

const getProjectTaskNotifications = (notifications: Notification[], projectId: string) => [
    ...getProjectNotificationsByType(notifications, projectId, 'task'),
    ...getProjectNotificationsByType(notifications, projectId, 'subtask')
];
export const makeGetProjectTaskNotifications = () => createSelector(getProjectTaskNotifications, notifications => notifications);

const getProjectDiscussionNotifications = (notifications: Notification[], projectId: string) => [
    ...getProjectNotificationsByType(notifications, projectId, 'message'),
    ...getProjectNotificationsByType(notifications, projectId, 'thread' )
];
export const makeGetProjectDiscussionNotifications = () => createSelector(getProjectDiscussionNotifications, notifications => notifications);

const getProjectDocNotifications = (notifications: Notification[], projectId: string) => getProjectNotificationsByType(notifications, projectId, 'document');
export const makeGetProjectDocNotifications = () => createSelector(getProjectDocNotifications, notifications => notifications);

const getProjectFileNotifications = (notifications: Notification[], projectId: string) => getProjectNotificationsByType(notifications, projectId, 'file');
export const makeGetProjectFileNotifications = () => createSelector(getProjectFileNotifications, notifications => notifications);

const getNotificationsByTaskIds = (notifications: Notification[], tasksIds: string[]) => filterNotifications(notifications, { tasksIds })
    .filter(notification => !NO_FLAG_ACTIVITIES.includes(`${notification.type}.${notification.action}`));
export const makeGetNotificationsByTaskIds = () => createSelector(getNotificationsByTaskIds, notifications => notifications);

const getAllNotificationsByTriggeredBy = (notifications: Notification[], triggeredBy: string) => notifications
    .filter(notification =>
        notification &&
        VALID_USER_ACTIVITIES.includes(`${notification.type}.${notification.action}`) &&
        notification.triggered_by?.id === triggeredBy
    );
export const makeGetAllNotificationsByTriggeredBy = () => createSelector(getAllNotificationsByTriggeredBy, notifications => notifications);

const getSortedPanelNotifications = (notifications: Notification[]) => notifications
    .filter(notification =>
        notification &&
        VALID_ACTIVITIES_FOR_PANEL.includes(`${notification.type}.${notification.action}`)
    )
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
export const makeGetSortedPanelNotifications = () => createSelector(getSortedPanelNotifications, notifications => notifications);

const getSortedInboxNotifications = (notifications: Notification[]) => notifications
    .filter(notification =>
        notification &&
        VALID_ACTIVITIES_FOR_INBOX.includes(`${notification.type}.${notification.action}`)
    )
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
export const makeGetSortedInboxNotifications = () => createSelector(getSortedInboxNotifications, notifications => notifications);

const getUnreadnboxNotificationsCount = (notifications: Notification[]) => notifications
    .filter(notification =>
        notification &&
        notificationIsUnread(notification) &&
        VALID_ACTIVITIES_FOR_INBOX.includes(`${notification.type}.${notification.action}`)
    ).length;
export const makeGetUnreadnboxNotificationsCount = () => createSelector(getUnreadnboxNotificationsCount, notifications => notifications);

const getPanelNotificationsCount = (notifications: Notification[]) => notifications
    .filter(notification =>
        notificationIsUnread(notification) &&
        VALID_ACTIVITIES_FOR_PANEL.includes(`${notification.type}.${notification.action}`)
    ).length;
export const makeGetPanelNotificationsCount = () => createSelector(getPanelNotificationsCount, notificationsCount => notificationsCount);

const getUnreadNotificationsByTypeAndAction = (notifications: Notification[], typesAndActions: string[]) => notifications
    .filter(notification => notificationIsUnread(notification) && typesAndActions.includes(`${notification.type}.${notification.action}`));
export const makeGetUnreadNotificationsByTypeAndAction = () => createSelector(getUnreadNotificationsByTypeAndAction, notifications => notifications);

const getUnreadNotificationsByAssociationType = (notifications: Notification[], associationType: string) => notifications
    .filter(notification => notificationIsUnread(notification) && notification.association && notification.association.type === associationType);
export const makeGetUnreadNotificationsByAssociationType = () => createSelector(getUnreadNotificationsByAssociationType, notifications => notifications);

export const makeGetUnreadActivities = () => createSelector(getUnreadActivities, notifications => notifications);

const getUnreadNotifications = (notifications: Notification[]) => notifications.filter(notificationIsUnread);
export const makeGetUnreadNotifications = () => createSelector(getUnreadNotifications, notifications => notifications);

const getNotificationsForProject = (notifications: Notification[], projectId: string) => getNotificationsForProjects(notifications, [projectId]);
export const makeGetNotificationsForProject = () => createSelector(getNotificationsForProject, notifications => notifications);

const getProjectNotificationsForSubteam = (notifications: Notification[], projects: Project[], subteamId: string) => getNotificationsForProjects(notifications, projects.filter(project => project?.subteam === subteamId).map(p => p.id));
export const makeGetProjectNotificationsForSubteam = () => createSelector(getProjectNotificationsForSubteam, notifications => notifications);

export const makeGetLastUserActivity = () => createSelector(getLastUserActivity, notifications => notifications);

const getFolderNotifications = (notifications: Notification[], folderId: string) => notifications
    .filter(notification => 
        notification?.association?.folder_stack?.includes(folderId) &&
        notificationIsUnread(notification)
    );
export const makeGetFolderNotifications = () => createSelector(getFolderNotifications, notifications => notifications);
